import { useEffect, useMemo, useState } from "react";
import {
  Field,
  FrameEmptyState,
  FrameErrorState,
  Input,
  Label,
  Select,
  StructuredField,
  StructuredGrid,
  StructuredHint,
  StructuredResult,
  StructuredResultHelp,
  StructuredResultLabel,
  StructuredResultSecondary,
  StructuredResultSecondaryLabel,
  StructuredResultSecondaryValue,
  StructuredResultValue,
  StructuredWrapper,
} from "../Calculator.style";
import { getLatestPatientResult } from "../../../api/patientApi";

function calculateAge(fechaNacimiento) {
  if (!fechaNacimiento) return null;
  const now = new Date();
  const birth = new Date(fechaNacimiento);
  if (Number.isNaN(birth.getTime())) return null;

  let age = now.getFullYear() - birth.getFullYear();
  const monthDiff = now.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birth.getDate())) {
    age -= 1;
  }

  return age;
}

function parseJsonSafe(value, fallback) {
  if (!value) return fallback;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
}

function resolveResultColor(...candidates) {
  for (const candidate of candidates) {
    const normalized = String(candidate || "").trim();
    if (/^#[0-9a-fA-F]{3,8}$/.test(normalized)) {
      return normalized;
    }
  }
  return "#0f172a";
}

function resolvePatientValue(patient, sourceKey) {
  if (!patient || !sourceKey) return "";
  const normalized = String(sourceKey).replace(/^patient\./, "");
  const map = {
    nombre: patient.nombre || "",
    tipoId: patient.tipoId || "",
    tipo_id: patient.tipoId || "",
    identificacion: patient.identificacion || "",
    sexo: patient.sexo || "",
    fechaNacimiento: patient.fechaNacimiento || "",
    fecha_nacimiento: patient.fechaNacimiento || "",
    edad: patient.edad ?? null,
  };
  return map[normalized] ?? "";
}

function resolveDerivedValue(variable, patient) {
  const key = variable.source_key || variable.variable_key || "";
  const normalized = String(key).replace(/^patient\./, "");

  if (normalized === "fechaNacimiento" || normalized === "fecha_nacimiento") {
    return calculateAge(patient?.fechaNacimiento);
  }

  if (normalized === "edad") {
    return patient?.edad ?? calculateAge(patient?.fechaNacimiento);
  }

  if (variable.variable_key === "edad") {
    return patient?.edad ?? calculateAge(patient?.fechaNacimiento);
  }

  return "";
}

function resolveConstantValue(variable) {
  if (variable.default_value !== undefined && variable.default_value !== "") {
    return variable.default_value;
  }
  return variable.source_key || "";
}

function toNumber(value) {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}

export default function StructuredCalculatorRenderer({ calculator, patient }) {
  const [values, setValues] = useState({});
  const [loadingApiValues, setLoadingApiValues] = useState(false);
  const [apiError, setApiError] = useState("");
  const [formulaError, setFormulaError] = useState("");
  const [result, setResult] = useState(null);

  const variables = useMemo(
    () => [...(calculator?.variables || [])].sort((a, b) => (a.orden || 0) - (b.orden || 0)),
    [calculator]
  );
  const resultConfig = useMemo(
    () => parseJsonSafe(calculator?.result_config, {}),
    [calculator?.result_config]
  );

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      if (!calculator || !patient) {
        setValues({});
        setResult(null);
        setApiError("");
        setFormulaError("");
        return;
      }

      setLoadingApiValues(true);
      setApiError("");
      setFormulaError("");

      const nextValues = {};

      variables.forEach((variable) => {
        let resolved = "";

        if (variable.source_type === "patient") {
          resolved = resolvePatientValue(patient, variable.source_key || variable.variable_key);
        } else if (variable.source_type === "derived") {
          resolved = resolveDerivedValue(variable, patient);
        } else if (variable.source_type === "constant") {
          resolved = resolveConstantValue(variable);
        } else if (variable.source_type === "manual") {
          resolved =
            variable.default_value !== undefined && variable.default_value !== ""
              ? variable.default_value
              : "";
        }

        nextValues[variable.variable_key] = resolved ?? "";
      });

      const apiVariables = variables.filter(
        (variable) =>
          variable.source_type === "api" &&
          variable.auto_fill &&
          variable.source_config?.consecutivo_id
      );

      try {
        for (const variable of apiVariables) {
          const consecutivo = variable.source_config?.consecutivo_id;
          const rs = await getLatestPatientResult(
            patient.identificacion,
            patient.tipoId,
            consecutivo
          );

          const latest = rs?.data?.GetLatestResult?.[0] || null;
          const fieldName = variable.source_config?.response_field || "Data_APIM1";
          const resolvedValue = latest?.[fieldName] ?? "";

          const shouldApply =
            variable.prefill_mode === "always" ||
            variable.prefill_mode === "initial" ||
            nextValues[variable.variable_key] === "" ||
            nextValues[variable.variable_key] === null;

          if (shouldApply) {
            nextValues[variable.variable_key] = resolvedValue;
          }
        }
      } catch (error) {
        if (!cancelled) {
          setApiError("No fue posible autollenar uno o más resultados clínicos.");
        }
      } finally {
        if (!cancelled) {
          setValues(nextValues);
          setLoadingApiValues(false);
        }
      }
    };

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, [calculator, patient, variables]);

  useEffect(() => {
    if (!calculator?.formula_js) {
      setResult(null);
      setFormulaError("");
      return;
    }

    try {
      const evaluator = new Function(
        "data",
        "context",
        `
          ${calculator.formula_js}
        `
      );

      const evaluated = evaluator(values, {
        patient,
        utils: {
          toNumber,
          calculateAge,
        },
      });

      if (evaluated === undefined || evaluated === null || evaluated === "") {
        setResult(null);
        setFormulaError("");
        return;
      }

      if (typeof evaluated === "object") {
        setResult(evaluated);
      } else {
        setResult({ value: evaluated });
      }
      setFormulaError("");
    } catch (error) {
      setResult(null);
      setFormulaError(error.message || "Error ejecutando la fórmula");
    }
  }, [calculator?.formula_js, patient, values]);

  const updateValue = (variableKey, value) => {
    setValues((prev) => ({
      ...prev,
      [variableKey]: value,
    }));
  };

  const resultMode = resultConfig?.mode || "value_unit";
  const resultLabel = result?.label || resultConfig?.label || "Resultado";
  const resultUnit = result?.unit || resultConfig?.unit || "";
  const resultMessage = result?.message || resultConfig?.message || "";
  const resultHelp = result?.help || resultConfig?.help || "";
  const secondaryTitle = result?.secondary_title || resultConfig?.secondary_title || "";
  const secondaryValue = result?.secondary_value ?? "";
  const showSecondary = resultMode === "cards" && Boolean(secondaryTitle || secondaryValue);
  const resultColor = resolveResultColor(result?.color, resultConfig?.color);

  if (!calculator) {
    return (
      <FrameEmptyState>
        Selecciona una calculadora disponible para el paciente.
      </FrameEmptyState>
    );
  }

  if (!patient) {
    return <FrameEmptyState>Selecciona un paciente para ejecutar la calculadora.</FrameEmptyState>;
  }

  return (
    <StructuredWrapper>
      {apiError ? <FrameErrorState>{apiError}</FrameErrorState> : null}
      {formulaError ? <FrameErrorState>{formulaError}</FrameErrorState> : null}

      <StructuredGrid>
        {variables
          .filter((variable) => Number(variable.visible) === 1)
          .map((variable) => {
            const options = parseJsonSafe(variable.options_json, []);
            const layout = parseJsonSafe(variable.layout_json, {});
            const value = values[variable.variable_key] ?? "";
            const readOnly =
              Number(variable.editable) !== 1 ||
              (variable.source_type === "api" &&
                Number(variable.allow_manual_override) !== 1 &&
                Number(variable.editable) !== 1);

            return (
              <StructuredField
                key={variable.variable_key}
                $span={Number(layout?.span || 1)}
              >
                <Label>
                  {variable.label}
                </Label>

                {variable.field_type === "select" ? (
                  <Select
                    value={value}
                    onChange={(e) => updateValue(variable.variable_key, e.target.value)}
                    disabled={readOnly}
                  >
                    <option value="">Selecciona</option>
                    {Array.isArray(options)
                      ? options.map((option, index) => {
                          const normalized =
                            typeof option === "object"
                              ? option
                              : { label: String(option), value: String(option) };
                          return (
                            <option
                              key={`${variable.variable_key}-${index}`}
                              value={normalized.value}
                            >
                              {normalized.label}
                            </option>
                          );
                        })
                      : null}
                  </Select>
                ) : (
                  <Input
                    type={variable.field_type === "number" ? "number" : "text"}
                    value={value}
                    readOnly={readOnly}
                    onChange={(e) => updateValue(variable.variable_key, e.target.value)}
                  />
                )}

                {variable.source_type === "api" && Number(variable.auto_fill) === 1 ? (
                  <StructuredHint>
                    {loadingApiValues
                      ? "Consultando valor sugerido..."
                      : Number(variable.allow_manual_override) === 1
                        ? "Valor sugerido cargado automaticamente. Puedes modificarlo."
                        : "Valor cargado automaticamente desde resultados."}
                  </StructuredHint>
                ) : null}
              </StructuredField>
            );
          })}
      </StructuredGrid>

      {result?.value !== undefined && result?.value !== null && result?.value !== "" ? (
        <StructuredResult>
          <StructuredResultLabel>{resultLabel}</StructuredResultLabel>

          {resultMode === "text" ? (
            <StructuredResultValue $color={resultColor}>
              {resultMessage || result.value}
            </StructuredResultValue>
          ) : (
            <StructuredResultValue $color={resultColor}>
              {result.value}
              {resultUnit ? ` ${resultUnit}` : ""}
            </StructuredResultValue>
          )}

          {showSecondary ? (
            <StructuredResultSecondary>
              <StructuredResultSecondaryLabel>{secondaryTitle}</StructuredResultSecondaryLabel>
              <StructuredResultSecondaryValue>{secondaryValue}</StructuredResultSecondaryValue>
            </StructuredResultSecondary>
          ) : null}

          {resultHelp ? <StructuredResultHelp>{resultHelp}</StructuredResultHelp> : null}
        </StructuredResult>
      ) : null}
    </StructuredWrapper>
  );
}
